import { ActivatedRoute, Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment as env } from '@env/environment';
import { HttpErrorHandler, HandleError } from './http-error-handler';
import { tap } from 'rxjs/internal/operators/tap';
import { AuthenticationService } from '../services/AuthenticationService';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { IFile } from '../interfaces/IFile';

@Injectable()
export class FileService {
    public apiAuthUrl: string;
    private apiBaseUrl: string;
    public token: string;
    private userString: string;
    private httpOptions: any;
    private handleError: HandleError;

    constructor(
        private http: HttpClient,
        private router: Router,
        private authenticationService: AuthenticationService,
        httpErrorHandler: HttpErrorHandler,
        public snackBar: MatSnackBar,
        private route: ActivatedRoute)
    {
        this.apiBaseUrl = env.hostApi;
        this.handleError = httpErrorHandler.createHandleError('FileService');
        this.setHttpOptions();
    }

    showMessage(message: string)
    {
        let config = new MatSnackBarConfig();
        config.duration = 7000;
        this.snackBar.open(message, undefined, config);
    }

    getFileTypes(): Observable<any> {
        var url = this.apiBaseUrl + 'FileTypes';

        return this.http.get<any>(url, this.httpOptions)
            .pipe(
            tap((response: any) => this.onFinished(response)),
            catchError(
                (err: any) => {
                    return new Observable<any>();
                }
            ));
    }

    getPublicFileTypes(): Observable<any> {
      var url = this.apiBaseUrl + 'PublicFileTypes';

      return this.http.get<any>(url, this.httpOptions)
        .pipe(
          tap((response: any) => this.onFinished(response)),
          catchError(
            (err: any) => {
              return new Observable<any>();
            }
          ));
    }

    deleteDocument(id: number): Observable<any> {
      var url = this.apiBaseUrl + 'Documents/' + id.toString();

      return this.http.delete<any>(url, this.httpOptions)
        .pipe(
          tap((response: any) => this.onFinished(response)),
          catchError(
            (err: any) => {
              return new Observable<any>();
            }
          ));
    }

    getDocuments(filter: any): Observable<any> {
      var url = this.apiBaseUrl + 'Documents?Skip=' + filter.Skip + '&Take=' + filter.Take + '&Asc=' + filter.Asc + '&Filter=' + filter.Filter + '&OrderBy=' + filter.OrderBy;

      return this.http.get<any>(url, this.httpOptions)
        .pipe(
          tap((response: any) => this.onFinished(response)),
          catchError(
            (err: any) => {
              return new Observable<any>();
            }
          ));
    }

    openFile(file: IFile | null | undefined): void {
      if (!file) {
        this.showMessage('Filen kunde inte hittas.');
        return;
      }

      const protectedGuid = file.Guid;

      if (protectedGuid) {
        this.openProtectedFile(protectedGuid, file.Name);
        return;
      }

      if (file.Url) {
        window.open(file.Url, '_blank');
        return;
      }

      this.showMessage('Filen saknar nedladdningslänk.');
    }

    openProtectedFile(guid: string, fileName?: string): void {
      if (!guid) {
        this.showMessage('Filen saknar identifierare.');
        return;
      }

      const url = this.buildProtectedFileUrl(guid);
      const viewerWindow = window.open('', '_blank');

      this.http.get(url, {
        observe: 'response',
        responseType: 'blob'
      }).subscribe({
        next: (response) => {
          this.openBlobResponse(response, fileName, viewerWindow).catch(() => {
            if (viewerWindow && !viewerWindow.closed) {
              viewerWindow.close();
            }
            this.showMessage('Filen kunde inte öppnas.');
          });
        },
        error: () => {
          if (viewerWindow && !viewerWindow.closed) {
            viewerWindow.close();
          }

          this.showMessage('Filen kunde inte öppnas.');
        }
      });
    }

    buildProtectedFileUrl(guid: string): string {
      return this.apiBaseUrl + 'Files/' + encodeURIComponent(guid) + '/download';
    }

    buildPublicDocumentUrl(relativePath: string): string {
      const baseUrl = (env.publicDocumentBaseUrl || '').replace(/\/+$/, '');
      const normalizedPath = (relativePath || '').replace(/^\/+/, '');
      return baseUrl + '/' + normalizedPath;
    }

    private async openBlobResponse(response: HttpResponse<Blob>, fileName?: string, viewerWindow?: Window | null): Promise<void> {
      const blob = response.body;
      if (!blob) {
        this.showMessage('Filen kunde inte öppnas.');
        return;
      }

      const resolvedFileName = this.resolveDownloadFileName(response, fileName);
      const displayBlob = await this.createDisplayBlob(blob, resolvedFileName);
      const blobUrl = window.URL.createObjectURL(displayBlob);

      if (viewerWindow && !viewerWindow.closed) {
        viewerWindow.location.href = blobUrl;
      } else {
        window.open(blobUrl, '_blank');
      }

      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 60000);
    }

    private async createDisplayBlob(blob: Blob, fileName: string): Promise<Blob> {
      const header = new Uint8Array(await blob.slice(0, 16).arrayBuffer());
      const startsWith = (bytes: number[]) => bytes.every((value, index) => header[index] === value);
      let mimeType: string | null = null;

      if (startsWith([0x25, 0x50, 0x44, 0x46, 0x2d])) {
        mimeType = 'application/pdf';
      } else if (startsWith([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) {
        mimeType = 'image/png';
      } else if (startsWith([0xff, 0xd8, 0xff])) {
        mimeType = 'image/jpeg';
      } else if (startsWith([0x47, 0x49, 0x46, 0x38, 0x37, 0x61]) || startsWith([0x47, 0x49, 0x46, 0x38, 0x39, 0x61])) {
        mimeType = 'image/gif';
      }

      if (!mimeType && (!blob.type || blob.type === 'application/octet-stream')) {
        mimeType = this.resolveMimeType(fileName);
      }

      return mimeType && mimeType !== blob.type ? new Blob([blob], { type: mimeType }) : blob;
    }

    private resolveMimeType(fileName: string): string | null {
      const extension = (fileName.split('.').pop() || '').toLowerCase();

      switch (extension) {
        case 'pdf':
          return 'application/pdf';
        case 'jpg':
        case 'jpeg':
          return 'image/jpeg';
        case 'png':
          return 'image/png';
        case 'gif':
          return 'image/gif';
        default:
          return null;
      }
    }

    private resolveDownloadFileName(response: HttpResponse<Blob>, preferredFileName?: string): string {
      const contentDisposition = response.headers.get('content-disposition');
      const headerFileName = this.extractFileNameFromContentDisposition(contentDisposition);
      const fileName = headerFileName || preferredFileName || 'document';

      if (fileName.lastIndexOf('.') > 0) {
        return fileName;
      }

      const mimeType = response.body?.type || '';
      if (mimeType === 'application/pdf') {
        return fileName + '.pdf';
      }

      return fileName;
    }

    private extractFileNameFromContentDisposition(contentDisposition: string | null): string | null {
      if (!contentDisposition) {
        return null;
      }

      const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
      if (utf8Match && utf8Match[1]) {
        return decodeURIComponent(utf8Match[1]).replace(/["]/g, '');
      }

      const asciiMatch = /filename="?([^\";]+)"?/i.exec(contentDisposition);
      if (asciiMatch && asciiMatch[1]) {
        return asciiMatch[1];
      }

      return null;
    }

    onFinished(response: any) {
        console.log(response);
    }

    setHttpOptions() {
        var bearerToken = 'bearer ' + this.authenticationService.token;

        this.httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': bearerToken
            })
        };
    }
}
